import React from 'react';
import type { FormType, AssessmentType } from '../types';

interface SurveyDetailsFormProps {
  editingFormId: string | null;
  formName: string;
  setFormName: (v: string) => void;
  formDescription: string;
  setFormDescription: (v: string) => void;
  formType: FormType;
  setFormType: (v: FormType) => void;
  assessmentType: AssessmentType;
  setAssessmentType: (v: AssessmentType) => void;
  expiryDate: string;
  setExpiryDate: (v: string) => void;
  maxResponses: string;
  setMaxResponses: (v: string) => void;
  onePerBrowser: boolean;
  setOnePerBrowser: (v: boolean) => void;
  collectName: boolean;
  setCollectName: (v: boolean) => void;
  collectEmail: boolean;
  setCollectEmail: (v: boolean) => void;
  showCorrectAnswers: boolean;
  setShowCorrectAnswers: (v: boolean) => void;
  showReport: boolean;
  setShowReport: (v: boolean) => void;
  objectiveRatio: number;
  setObjectiveRatio: (v: number) => void;
}

const SurveyDetailsForm: React.FC<SurveyDetailsFormProps> = ({
  editingFormId,
  formName, setFormName,
  formDescription, setFormDescription,
  formType, setFormType,
  assessmentType, setAssessmentType,
  expiryDate, setExpiryDate,
  maxResponses, setMaxResponses,
  onePerBrowser, setOnePerBrowser,
  collectName, setCollectName,
  collectEmail, setCollectEmail,
  showCorrectAnswers, setShowCorrectAnswers,
  showReport, setShowReport,
  objectiveRatio, setObjectiveRatio,
}) => {
  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value as FormType;
    setFormType(val);
    if (val === 'assessment') {
      setAssessmentType('objective');
    }
  };

  const handleSubjectiveToggle = (checked: boolean) => {
    if (checked) {
      setAssessmentType(assessmentType === 'objective' ? 'both' : 'subjective');
    } else {
      setAssessmentType(assessmentType === 'both' ? 'objective' : 'objective');
    }
  };

  const handleObjectiveToggle = (checked: boolean) => {
    if (checked) {
      setAssessmentType(assessmentType === 'subjective' ? 'both' : 'objective');
    } else {
      setAssessmentType(assessmentType === 'both' ? 'subjective' : 'subjective');
    }
  };

  const isObjectiveChecked = assessmentType === 'objective' || assessmentType === 'both';
  const isSubjectiveChecked = assessmentType === 'subjective' || assessmentType === 'both';

  return (
    <div className="admin-feedback-survey-details-section">
      <h3>{editingFormId ? 'Edit Survey' : 'Create New Survey'}</h3>

      <div className="admin-feedback-form-field">
        <label htmlFor="survey-name">Survey Name *</label>
        <input
          type="text"
          id="survey-name"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          placeholder="e.g., Course Feedback Survey"
        />
      </div>

      <div className="admin-feedback-form-field">
        <label htmlFor="description">Description *</label>
        <textarea
          id="description"
          value={formDescription}
          onChange={(e) => setFormDescription(e.target.value)}
          rows={3}
          placeholder="Brief description of the survey purpose"
        />
      </div>

      <div className="admin-feedback-form-row">
        <div className="admin-feedback-form-field">
          <label htmlFor="survey-type">Survey Type *</label>
          <select
            id="survey-type"
            value={formType}
            onChange={handleTypeChange}
          >
            <option value="feedback">Feedback</option>
            <option value="satisfaction">Satisfaction</option>
            <option value="assessment">Assessment</option>
          </select>
        </div>

        <div className="admin-feedback-form-field">
          <label htmlFor="expiry-date">Expiry Date *</label>
          <input
            type="datetime-local"
            id="expiry-date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
        </div>
      </div>

      {formType === 'assessment' && (
        <div className="admin-feedback-form-row">
          <div className="admin-feedback-form-field admin-feedback-checkbox-group">
            <label style={{ marginBottom: '8px', fontWeight: 500 }}>Assessment Type *</label>
            <label className="admin-feedback-checkbox-label">
              <input
                type="checkbox"
                checked={isObjectiveChecked}
                onChange={(e) => handleObjectiveToggle(e.target.checked)}
              />
              <span>Objective Assessment</span>
            </label>
            <label className="admin-feedback-checkbox-label" style={{ marginTop: '6px' }}>
              <input
                type="checkbox"
                checked={isSubjectiveChecked}
                onChange={(e) => handleSubjectiveToggle(e.target.checked)}
              />
              <span>Subjective Assessment</span>
            </label>
          </div>

          {assessmentType === 'both' && (
            <div className="admin-feedback-form-field" style={{ marginLeft: '12px' }}>
              <label style={{ marginBottom: '8px', fontWeight: 500 }}>
                Question Ratio (Objective : Subjective)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1976d2', minWidth: '32px', textAlign: 'center' }}>
                  {objectiveRatio}%
                </span>
                <input
                  type="range"
                  min={10}
                  max={90}
                  step={10}
                  value={objectiveRatio}
                  onChange={(e) => setObjectiveRatio(parseInt(e.target.value))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#9c27b0', minWidth: '32px', textAlign: 'center' }}>
                  {100 - objectiveRatio}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '4px' }}>
                <span>Objective</span>
                <span>Subjective</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="admin-feedback-form-row">
        <div className="admin-feedback-form-field">
          <label htmlFor="max-responses">Max Responses (Optional)</label>
          <input
            type="number"
            id="max-responses"
            value={maxResponses}
            onChange={(e) => setMaxResponses(e.target.value)}
            placeholder="Unlimited"
            min="1"
          />
        </div>

        <div className="admin-feedback-form-field admin-feedback-checkbox-group">
          <label className="admin-feedback-checkbox-label">
            <input
              type="checkbox"
              checked={onePerBrowser}
              onChange={(e) => setOnePerBrowser(e.target.checked)}
            />
            <span>One response per browser</span>
          </label>
        </div>
      </div>

      <div className="admin-feedback-form-row">
        <div className="admin-feedback-form-field admin-feedback-checkbox-group">
          <label className="admin-feedback-checkbox-label">
            <input
              type="checkbox"
              checked={collectName}
              onChange={(e) => setCollectName(e.target.checked)}
            />
            <span>Collect respondent name</span>
          </label>
        </div>

        <div className="admin-feedback-form-field admin-feedback-checkbox-group">
          <label className="admin-feedback-checkbox-label">
            <input
              type="checkbox"
              checked={collectEmail}
              onChange={(e) => setCollectEmail(e.target.checked)}
            />
            <span>Collect respondent email</span>
          </label>
        </div>
      </div>

      {formType === 'assessment' && (
        <div className="admin-feedback-form-row">
          <div className="admin-feedback-form-field admin-feedback-checkbox-group">
            <label className="admin-feedback-checkbox-label admin-feedback-toggle-label">
              <input
                type="checkbox"
                checked={showCorrectAnswers}
                onChange={(e) => setShowCorrectAnswers(e.target.checked)}
              />
              <span>Show correct answers after submission</span>
            </label>
            <p className="admin-feedback-field-hint">
              When disabled, respondents will only see their score, not the correct answers.
            </p>
          </div>

          <div className="admin-feedback-form-field admin-feedback-checkbox-group">
            <label className="admin-feedback-checkbox-label admin-feedback-toggle-label">
              <input
                type="checkbox"
                checked={showReport}
                onChange={(e) => setShowReport(e.target.checked)}
              />
              <span>Show report/score upon submission</span>
            </label>
            <p className="admin-feedback-field-hint">
              When enabled, respondents will see their score report after completing the assessment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(SurveyDetailsForm);
